/**
 * The SLA card for the drawer's INSIGHTS lens (#327 / #337).
 *
 * The Elevate-style sidepane follows whatever record is on screen. On a support
 * case the one number that matters is not a pipeline figure — it is whether a
 * commitment is about to expire. Before this, opening a case in any surface gave
 * the lens's CRM block, which for a case is simply not about anything.
 *
 * Read from `service.sla_board(case_id)`, where breach is computed live: this is
 * correct even when the escalation sweep is not running, which is exactly the
 * condition under which someone is most likely to be reading it.
 */
import { FactList, Pill } from '@solum/ui'
import { useSlaBoard, type SlaClock } from '../data/queries'
import { queryState, unresolvedText } from '../data/QueryBoundary'
import { untilLabel, fmtWhen, obligationLabel } from './sla'

export function SlaInsight({ id }: { id: string }) {
  const board = useSlaBoard(id)
  const { kind } = queryState([board])
  const clocks = (kind === 'ready' ? board.data ?? [] : []) as SlaClock[]
  const running = clocks.filter((k) => k.status === 'running')
  const breached = running.filter((k) => k.breached)
  // the soonest live commitment — the same "jeopardy" the queues sort by
  const next = [...running].sort((a, b) => String(a.due_at).localeCompare(String(b.due_at)))[0]

  // Before #389 this returned `null` while pending and then fell through to
  // "No commitment" on FAILURE — so a refused or broken read of `sla_board`
  // rendered the lens's most reassuring possible answer. The card keeps its shape
  // in every state instead: the lens should not blink out and reappear, and it
  // must never state an absence it did not observe.
  if (kind !== 'ready') {
    return (
      <FactList framed data-testid="insight-sla-unresolved" data-state={kind} facts={[{
        key: 'sla',
        label: 'SLA',
        value: kind === 'loading' ? '…' : 'Unknown',
        hint: unresolvedText(kind, 'the commitments on this case'),
      }]} />
    )
  }
  if (clocks.length === 0) {
    return (
      <FactList framed data-testid="insight-sla-none" facts={[{
        key: 'sla',
        label: 'SLA',
        value: 'No commitment',
        hint: 'This case carries no SLA policy — a declared absence, not a met deadline.',
      }]} />
    )
  }

  return (
    <FactList framed data-testid="insight-sla" facts={[{
      key: 'sla',
      label: 'SLA',
      value: breached.length > 0
        ? <Pill tone="critical">{breached.length === 1 ? 'Breached' : `${breached.length} breached`}</Pill>
        : running.length === 0
          ? <Pill tone={clocks.some((k) => k.satisfied_late) ? 'warn' : 'good'}>
              {clocks.some((k) => k.satisfied_late) ? 'Met late' : 'Met'}
            </Pill>
          : <Pill tone="neutral">{untilLabel(next?.due_at)}</Pill>,
      /* The testid rides on the caption's own element rather than becoming a
         prop on `Fact`: `hint` is a ReactNode, so a host that needs a locator
         supplies one, and the kit does not grow an API for one caller. */
      hint: <span data-testid="insight-sla-detail">{breached.length > 0
        ? `${obligationLabel(breached[0]!.obligation)} expired ${fmtWhen(breached[0]!.due_at)} with nothing recorded.`
        : next
          ? `${obligationLabel(next.obligation)} due ${fmtWhen(next.due_at)} · ${next.sla_label ?? next.sla_key ?? '—'}.`
          : 'Every commitment on this case is settled.'}</span>,
    }]} />
  )
}
